"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Ambulance,
  Apple,
  ArrowLeft,
  BookOpenText,
  BriefcaseBusiness,
  Cross,
  Facebook,
  FireExtinguisher,
  GraduationCap,
  HeartPulse,
  Instagram,
  Microscope,
  Pencil,
  Pill,
  School,
  ShieldCheck,
  Siren,
  Smile,
  Sparkles,
  SquareUserRound,
  Stethoscope,
  UserRound,
  Youtube,
} from "lucide-react";
import posthog from "posthog-js";
import type { ComponentType } from "react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  BlackGoogleIcon,
  ChatGPTIcon,
  RedditIcon,
  TikTokIcon,
} from "@/components/ui/icons";
import { useUserMutation } from "@/features/users/hooks/use-user";
import { readAttribution } from "@/lib/attribution";
import { cn } from "@/lib/utils";

const studentTypeValues = [
  "undergraduate",
  "graduate",
  "highSchool",
  "healthRelated",
  "teacher",
  "workingProfessional",
  "firstResponder",
  "other",
] as const;

const healthStudentTypeValues = [
  "preMed",
  "medical",
  "dental",
  "nursing",
  "pharmacy",
  "otherHealth",
] as const;

const firstResponderTypeValues = [
  "fireDepartment",
  "ems",
  "lawEnforcement",
  "otherFirstResponder",
] as const;

const referralSourceValues = [
  "friend",
  "search",
  "instagram",
  "tiktok",
  "youtube",
  "reddit",
  "facebook",
  "aiSearch",
  "teacherReferral",
  "otherReferral",
] as const;

const formSchema = z
  .object({
    studentType: z.enum(studentTypeValues),
    healthStudentType: z.enum(healthStudentTypeValues).optional(),
    firstResponderType: z.enum(firstResponderTypeValues).optional(),
    referralSource: z.enum(referralSourceValues),
  })
  .superRefine((values, ctx) => {
    if (values.studentType === "healthRelated" && !values.healthStudentType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["healthStudentType"],
        message: "Please select the type of health student you are",
      });
    }

    if (values.studentType === "firstResponder" && !values.firstResponderType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["firstResponderType"],
        message: "Please select the type of first responder you are",
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;
type StudentTypeValue = (typeof studentTypeValues)[number];
type HealthStudentTypeValue = (typeof healthStudentTypeValues)[number];
type FirstResponderTypeValue = (typeof firstResponderTypeValues)[number];
type ReferralSourceValue = (typeof referralSourceValues)[number];

type OptionIcon = ComponentType<{ className?: string }>;

type StepOption<TValue extends string = string> = {
  value: TValue;
  label: string;
  icon: OptionIcon;
};

const primaryStudentOptions: StepOption<StudentTypeValue>[] = [
  {
    value: "undergraduate",
    label: "Undergraduate",
    icon: GraduationCap,
  },
  {
    value: "graduate",
    label: "Graduate",
    icon: BookOpenText,
  },
  {
    value: "highSchool",
    label: "High School",
    icon: Pencil,
  },
  {
    value: "healthRelated",
    label: "Health Related",
    icon: Cross,
  },
];

const secondaryStudentOptions: StepOption<StudentTypeValue>[] = [
  {
    value: "teacher",
    label: "Teacher",
    icon: Apple,
  },
  {
    value: "workingProfessional",
    label: "Working Professional",
    icon: BriefcaseBusiness,
  },
  {
    value: "firstResponder",
    label: "First Responder",
    icon: ShieldCheck,
  },
  {
    value: "other",
    label: "Other",
    icon: SquareUserRound,
  },
];

const healthStudentOptions: StepOption<HealthStudentTypeValue>[] = [
  {
    value: "preMed",
    label: "Pre-med",
    icon: Microscope,
  },
  {
    value: "medical",
    label: "Medical",
    icon: Stethoscope,
  },
  {
    value: "dental",
    label: "Dental",
    icon: Sparkles,
  },
  {
    value: "nursing",
    label: "Nursing",
    icon: HeartPulse,
  },
  {
    value: "pharmacy",
    label: "Pharmacy",
    icon: Pill,
  },
  {
    value: "otherHealth",
    label: "Other Health Related",
    icon: SquareUserRound,
  },
];

const firstResponderOptions: StepOption<FirstResponderTypeValue>[] = [
  {
    value: "fireDepartment",
    label: "Fire Department",
    icon: FireExtinguisher,
  },
  {
    value: "ems",
    label: "Emergency Medical (EMS)",
    icon: Ambulance,
  },
  {
    value: "lawEnforcement",
    label: "Law Enforcement",
    icon: Siren,
  },
  {
    value: "otherFirstResponder",
    label: "Other First Responder",
    icon: UserRound,
  },
];

const referralOptions: StepOption<ReferralSourceValue>[] = [
  {
    value: "friend",
    label: "Friend / Classmate",
    icon: Smile,
  },
  {
    value: "search",
    label: "Google / Search Engine",
    icon: BlackGoogleIcon,
  },
  {
    value: "instagram",
    label: "Instagram",
    icon: Instagram,
  },
  {
    value: "tiktok",
    label: "TikTok",
    icon: TikTokIcon,
  },
  {
    value: "youtube",
    label: "YouTube",
    icon: Youtube,
  },
  {
    value: "reddit",
    label: "Reddit",
    icon: RedditIcon,
  },
  {
    value: "facebook",
    label: "Facebook",
    icon: Facebook,
  },
  {
    value: "aiSearch",
    label: "ChatGPT / AI Search",
    icon: ChatGPTIcon,
  },
  {
    value: "teacherReferral",
    label: "Teacher",
    icon: School,
  },
  {
    value: "otherReferral",
    label: "Other",
    icon: SquareUserRound,
  },
];

export type OnboardingStep =
  | "learner"
  | "health"
  | "firstResponder"
  | "referral";

interface StudentTypeFormProps {
  onSuccess: () => void;
  onStepChange: (step: OnboardingStep, progressIndex: number) => void;
}

export const StudentTypeForm = ({
  onSuccess,
  onStepChange,
}: StudentTypeFormProps) => {
  const [step, setStep] = useState<OnboardingStep>("learner");
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const updateUserMutation = useUserMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      studentType: undefined,
      referralSource: undefined,
    },
  });

  const selectedStudentType = form.watch("studentType");
  const requiresSpecialization =
    selectedStudentType === "healthRelated" ||
    selectedStudentType === "firstResponder";

  const currentStepIndex = useMemo(() => {
    if (step === "learner") return 0;
    if (step === "health" || step === "firstResponder") return 1;
    return requiresSpecialization ? 2 : 1;
  }, [requiresSpecialization, step]);

  useEffect(() => {
    onStepChange(step, currentStepIndex);
  }, [step, currentStepIndex, onStepChange]);

  const isPending = form.formState.isSubmitting;

  const handleStudentSelection = (value: StudentTypeValue) => {
    if (isPending) return;
    setSubmissionError(null);
    form.setValue("studentType", value, { shouldValidate: true });
    form.resetField("referralSource");

    if (value === "healthRelated") {
      form.resetField("firstResponderType");
      setStep("health");
      return;
    }

    if (value === "firstResponder") {
      form.resetField("healthStudentType");
      setStep("firstResponder");
      return;
    }

    form.resetField("healthStudentType");
    form.resetField("firstResponderType");
    setStep("referral");
  };

  const handleHealthSelection = (value: HealthStudentTypeValue) => {
    if (isPending) return;
    form.setValue("healthStudentType", value, { shouldValidate: true });
    form.resetField("referralSource");
    setStep("referral");
  };

  const handleFirstResponderSelection = (value: FirstResponderTypeValue) => {
    if (isPending) return;
    form.setValue("firstResponderType", value, { shouldValidate: true });
    form.resetField("referralSource");
    setStep("referral");
  };

  const handleReferralSelection = (value: ReferralSourceValue) => {
    if (isPending) return;
    setSubmissionError(null);
    form.setValue("referralSource", value, { shouldValidate: true });
    form.handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    if (isPending) return;
    setSubmissionError(null);
    if (step === "referral") {
      form.resetField("referralSource");
      if (selectedStudentType === "healthRelated") {
        setStep("health");
        return;
      }
      if (selectedStudentType === "firstResponder") {
        setStep("firstResponder");
        return;
      }
      setStep("learner");
      return;
    }

    if (step === "health" || step === "firstResponder") {
      form.resetField("studentType");
      form.resetField("healthStudentType");
      form.resetField("firstResponderType");
      setStep("learner");
    }
  };

  const buildStudentTypeValue = (values: FormValues) => {
    if (values.studentType === "healthRelated" && values.healthStudentType) {
      return `${values.studentType}-${values.healthStudentType}`;
    }

    if (values.studentType === "firstResponder" && values.firstResponderType) {
      return `${values.studentType}-${values.firstResponderType}`;
    }

    return values.studentType;
  };

  const onSubmit = async (values: FormValues) => {
    setSubmissionError(null);

    try {
      const attribution = readAttribution(document.cookie);
      await updateUserMutation.mutateAsync({
        studentType: buildStudentTypeValue(values),
        referralSource: values.referralSource,
        onboardedSteps: 2,
        ...(attribution
          ? {
              landingPage: attribution.landing_page,
              firstReferrer: attribution.referrer || undefined,
              utmSource: attribution.utm_source,
              utmMedium: attribution.utm_medium,
              utmCampaign: attribution.utm_campaign,
            }
          : {}),
      });

      posthog.setPersonProperties({
        student_type: buildStudentTypeValue(values),
        referral_source: values.referralSource,
      });
      onSuccess();
    } catch (error) {
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "Failed to save onboarding information. Please try again.",
      );
    }
  };

  const renderLearnerStep = () => (
    <div className="grid gap-8 md:grid-cols-2">
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-4">
          Student
        </p>
        <div className="space-y-3">
          {primaryStudentOptions.map((option) => (
            <OptionCard
              key={option.value}
              option={option}
              isSelected={selectedStudentType === option.value}
              onClick={() => handleStudentSelection(option.value)}
              disabled={isPending}
            />
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-4">
          More options
        </p>
        <div className="space-y-3">
          {secondaryStudentOptions.map((option) => (
            <OptionCard
              key={option.value}
              option={option}
              isSelected={selectedStudentType === option.value}
              onClick={() => handleStudentSelection(option.value)}
              disabled={isPending}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderHealthStep = () => (
    <div className="grid gap-3 sm:grid-cols-2">
      {healthStudentOptions.map((option) => (
        <OptionCard
          key={option.value}
          option={option}
          isSelected={form.watch("healthStudentType") === option.value}
          onClick={() => handleHealthSelection(option.value)}
          disabled={isPending}
        />
      ))}
    </div>
  );

  const renderFirstResponderStep = () => (
    <div className="grid gap-3 sm:grid-cols-2">
      {firstResponderOptions.map((option) => (
        <OptionCard
          key={option.value}
          option={option}
          isSelected={form.watch("firstResponderType") === option.value}
          onClick={() => handleFirstResponderSelection(option.value)}
          disabled={isPending}
        />
      ))}
    </div>
  );

  const renderReferralStep = () => (
    <div className="grid gap-3 sm:grid-cols-2">
      {referralOptions.map((option) => (
        <OptionCard
          key={option.value}
          option={option}
          isSelected={form.watch("referralSource") === option.value}
          onClick={() => handleReferralSelection(option.value)}
          disabled={isPending}
        />
      ))}
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case "health":
        return renderHealthStep();
      case "firstResponder":
        return renderFirstResponderStep();
      case "referral":
        return renderReferralStep();
      default:
        return renderLearnerStep();
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        {submissionError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {submissionError}
          </div>
        )}

        {renderCurrentStep()}

        {step !== "learner" && (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 w-fit"
              onClick={handleBack}
              disabled={isPending}
            >
              <ArrowLeft className="mr-2 size-4" />
              Back
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

interface OptionCardProps {
  option: StepOption;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const OptionCard = ({
  option,
  isSelected,
  onClick,
  disabled,
}: OptionCardProps) => {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full rounded-lg border bg-card py-4 px-3 text-left transition-all focus-visible:outline-none hover:border-brand-primary",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <div className="flex items-center gap-4">
        <Icon className="size-4 text-muted-foreground" />
        <p className="text-sm font-medium text-card-foreground">
          {option.label}
        </p>
      </div>
    </button>
  );
};
